import axios, { AxiosError, AxiosInstance } from 'axios';

export interface GalleryPictureContent {
    id: number;
    image: string;
    created_at: string;
    updated_at: string;
}

export interface CreateGalleryPictureData {
    image?: File;
}

export interface UpdateGalleryPictureData extends CreateGalleryPictureData {
    id: number;
}

class GalleryClass {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 10000,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createGalleryPicture(data: CreateGalleryPictureData): Promise<GalleryPictureContent | undefined> {
        try {
            const formData = new FormData();
            
            if (data.image instanceof File) {
                formData.append('image', data.image);
            }

            const response = await this.axiosInstance.post<{
                message: string;
                data: GalleryPictureContent;
            }>('/admin/gallery/picture/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to create gallery picture');
            return undefined;
        }
    }

    async getGalleryPicture(id: number): Promise<GalleryPictureContent | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: GalleryPictureContent;
            }>(`/gallery/picture/${id}`);
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve gallery picture');
            return undefined;
        }
    }

    async updateGalleryPicture(data: UpdateGalleryPictureData): Promise<GalleryPictureContent | undefined> {
        try {
            const formData = new FormData();
            
            if (data.image instanceof File) {
                formData.append('image', data.image);
            }

            const response = await this.axiosInstance.put<{
                message: string;
                data: GalleryPictureContent;
            }>(`/admin/gallery/picture/${data.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to update gallery picture');
            return undefined;
        }
    }

    async deleteGalleryPicture(id: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/gallery/picture/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete gallery picture');
        }
    }

    async getAllGalleryPictures(): Promise<GalleryPictureContent[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: GalleryPictureContent[];
            }>('/gallery/picture');

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve gallery pictures');
            return [];
        }
    }

    private handleError(error: unknown, message: string) {
        if (error instanceof AxiosError) {
            console.error(`${message}:`, error.response?.data);
            throw error.response?.data || { error: message };
        }
        console.error(message, error);
        throw { error: message };
    }
}

export const galleryClass = new GalleryClass();